/** @jsxRuntime automatic */
/** @jsxImportSource react */
import {
  createContext, useContext, useId,
  type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { ChevronDown, CircleAlert } from 'lucide-react';
import { Icon } from './Icon';

interface FieldWiring {
  id: string;
  describedBy?: string;
  invalid: boolean;
  changed: boolean;
}

const FieldContext = createContext<FieldWiring | null>(null);

export interface FieldProps {
  label: ReactNode;
  /** Help under the control. */
  help?: ReactNode;
  /** A refusal: replaces the help, in the danger colour, and marks the control invalid. */
  error?: ReactNode;
  /** The saved value while this one is unsaved: shows "Unsaved · was <value>" and an amber edge. */
  changedFrom?: ReactNode;
  /** Keep the label for assistive tech only (a control inside a table row). */
  labelHidden?: boolean;
  children: ReactNode;
  className?: string;
}

/** A label (13/500), the control, then help or an error (12 px), 6 px apart. The control inside picks up its wiring. */
export function Field({ label, help, error, changedFrom, labelHidden = false, children, className }: FieldProps) {
  const id = useId();
  const noteId = `${id}-note`;
  const hasNote = Boolean(error) || changedFrom !== undefined || Boolean(help);
  const wiring: FieldWiring = { id, describedBy: hasNote ? noteId : undefined, invalid: Boolean(error), changed: changedFrom !== undefined && !error };
  return (
    <div className={['ui-field', className ?? ''].filter(Boolean).join(' ')}>
      <label className={labelHidden ? 'ui-field-label ui-sr' : 'ui-field-label'} htmlFor={id}>{label}</label>
      <FieldContext.Provider value={wiring}>{children}</FieldContext.Provider>
      {error ? (
        <p className="ui-field-note is-error" id={noteId}><Icon icon={CircleAlert} size="sm" />{error}</p>
      ) : changedFrom !== undefined ? (
        <p className="ui-field-note is-changed" id={noteId}>Unsaved · was {changedFrom}</p>
      ) : help ? (
        <p className="ui-field-note" id={noteId}>{help}</p>
      ) : null}
    </div>
  );
}

function useWiring(id: string | undefined, describedBy: string | undefined, invalid: boolean | undefined, changed: boolean | undefined) {
  const field = useContext(FieldContext);
  return {
    id: id ?? field?.id,
    describedBy: [describedBy, field?.describedBy].filter(Boolean).join(' ') || undefined,
    invalid: invalid ?? field?.invalid ?? false,
    changed: changed ?? field?.changed ?? false,
  };
}

function controlClass(base: string, size: 'sm' | 'md', invalid: boolean, changed: boolean, extra?: string): string {
  return [base, size === 'sm' ? 'is-sm' : '', invalid ? 'is-invalid' : changed ? 'is-changed' : '', extra ?? ''].filter(Boolean).join(' ');
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  size?: 'sm' | 'md';
  invalid?: boolean;
  changed?: boolean;
  /** A unit after the value ("rounds", "USD"). */
  suffix?: ReactNode;
  /** A leading icon or symbol ("$", a search glass). */
  prefix?: ReactNode;
  /** The input element (React 19 passes a ref as a prop; it reaches the element with the rest). */
  ref?: Ref<HTMLInputElement>;
}

export function Input({ size = 'md', invalid, changed, suffix, prefix, className, id, 'aria-describedby': describedBy, ...rest }: InputProps) {
  const wiring = useWiring(id, describedBy, invalid, changed);
  const input = (
    <input
      id={wiring.id}
      aria-describedby={wiring.describedBy}
      aria-invalid={wiring.invalid || undefined}
      className={suffix || prefix ? 'ui-input-bare' : controlClass('ui-input', size, wiring.invalid, wiring.changed, className)}
      {...rest}
    />
  );
  if (!suffix && !prefix) return input;
  return (
    <div className={controlClass('ui-input ui-input-group', size, wiring.invalid, wiring.changed, className)}>
      {prefix ? <span className="ui-input-affix">{prefix}</span> : null}
      {input}
      {suffix ? <span className="ui-input-affix">{suffix}</span> : null}
    </div>
  );
}

/** A number typed as text (so a half-typed value is never thrown away), right-aligned in tabular figures. */
export function NumberInput({ className, inputMode = 'decimal', ...rest }: InputProps) {
  return <Input inputMode={inputMode} className={['is-num', className ?? ''].filter(Boolean).join(' ')} {...rest} />;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: 'sm' | 'md';
  invalid?: boolean;
  changed?: boolean;
}

export function Select({ size = 'md', invalid, changed, className, id, 'aria-describedby': describedBy, children, ...rest }: SelectProps) {
  const wiring = useWiring(id, describedBy, invalid, changed);
  return (
    <div className={controlClass('ui-select', size, wiring.invalid, wiring.changed, className)}>
      <select id={wiring.id} aria-describedby={wiring.describedBy} aria-invalid={wiring.invalid || undefined} className="ui-select-native" {...rest}>
        {children}
      </select>
      <Icon icon={ChevronDown} size="md" className="ui-select-chevron" />
    </div>
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  changed?: boolean;
}

export function Textarea({ invalid, changed, className, id, 'aria-describedby': describedBy, ...rest }: TextareaProps) {
  const wiring = useWiring(id, describedBy, invalid, changed);
  return (
    <textarea
      id={wiring.id}
      aria-describedby={wiring.describedBy}
      aria-invalid={wiring.invalid || undefined}
      className={controlClass('ui-input ui-textarea', 'md', wiring.invalid, wiring.changed, className)}
      {...rest}
    />
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
}

export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  return (
    <label className={['ui-check', className ?? ''].filter(Boolean).join(' ')}>
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** The switch's name (shown beside it unless `labelHidden`). */
  label: ReactNode;
  labelHidden?: boolean;
  disabled?: boolean;
  /** Words for the on and off states, read beside the switch ("On", "Off"). */
  stateLabels?: [ReactNode, ReactNode];
}

/** An on/off switch: a button with role="switch", so it is one tab stop and Space or Enter flips it. */
export function Switch({ checked, onChange, label, labelHidden = false, disabled = false, stateLabels }: SwitchProps) {
  const id = useId();
  return (
    <span className="ui-switch-row">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={id}
        className={`ui-switch${checked ? ' is-on' : ''}`}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        <span className="ui-switch-thumb" aria-hidden="true" />
      </button>
      <span id={id} className={labelHidden ? 'ui-sr' : 'ui-switch-label'}>{label}</span>
      {stateLabels ? <span className="ui-switch-state" aria-hidden="true">{checked ? stateLabels[0] : stateLabels[1]}</span> : null}
    </span>
  );
}
